import {
  Text,
  Flex,
  Spinner,
  Box,
  Table,
  Thead,
  Th,
  Tr,
  Tbody,
  Td,
  Skeleton,
  Tooltip,
  Menu,
  MenuButton,
  MenuList
} from '@chakra-ui/react';
import {
  Badge,
  Button,
  ButtonText,
  MenuItem,
  Modal,
  SearchInput,
  useToast
} from '@morressier/alembic';
import {
  faBars,
  faCheck,
  faEnvelope,
  faTrash,
  faUserPlus,
  faUserGroup,
  faSearch,
  faDeleteRight,
  faUserShield
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchWorkspaceMemberships, isSubmissionOwner } from 'src/api/queries';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import RemoveMember from './RemoveMember';
import { useEffect, useMemo, useState } from 'react';
import {
  addUsersToWorkspace,
  grantOrgAdminToUser,
  removeUserFromWorkspace,
  revertOrgAdminFromUser
} from 'src/api/mutations';
import AddMembers, { IWorkspaceMember } from './AddMembers';
import { useKeycloak } from 'src/components/AuthHOC';
import { useForm } from 'react-hook-form';
import SubmittedAuthorDetailsModal from 'src/components/Submission/AuthorsTab/AuthorProfileModal/SubmittedAuthorDetailsModal';
import { IWorkspaceMembership } from '@publishing-engine/domain/models';
import { useWorkspace } from '../../SubmissionContexts/Users/userHook';
import useAuthAccessQuery from 'src/hooks/useAuthAccessQuery';
import NotFound from 'src/pages/Status/NotFound';
import Forbidden from 'src/pages/Status/Forbidden';
import { isForbiddenError } from 'src/utils/queryClient';
import TablePagination, {
  DEFAULT_PAGE_SIZE
} from 'src/components/TablePagination';

interface IMemberFilter {
  searchParams: string;
}

type AdminAction = 'grant' | 'revert';

const defaultValues: IMemberFilter = {
  searchParams: ''
};

const adminActionCopy: Record<
  AdminAction,
  { title: string; body: string; buttonLabel: string; successTitle: string }
> = {
  grant: {
    title: 'Mark as Admin?',
    body: 'You are about to grant org admin permissions to this member. Do you wish to continue?',
    buttonLabel: 'Mark As Org Admin',
    successTitle: 'Org admin permissions granted successfully'
  },
  revert: {
    title: 'Revert Admin?',
    body: 'You are about to remove org admin permissions from this member. Their staff permissions will remain. Do you wish to continue?',
    buttonLabel: 'Remove Org Admin Access',
    successTitle: 'Org admin permissions reverted successfully'
  }
};

const userStatusBadgeMap = {
  invited: (
    <Badge size="context" variant="neutral" alignItems="center">
      <FontAwesomeIcon icon={faEnvelope} /> Invited
    </Badge>
  ),
  active: (
    <Badge size="context" variant="success" alignItems="center">
      <FontAwesomeIcon icon={faCheck} /> Active Member
    </Badge>
  ),
  deactivated: (
    <Badge size="context" variant="caution" alignItems="center">
      Deactivated
    </Badge>
  )
};

interface WorkspaceMembersPanelProps {
  workspaceId?: string;
}

const WorkspaceMembersPanel = ({
  workspaceId: providedWorkspaceId
}: WorkspaceMembersPanelProps) => {
  const { currentUser } = useKeycloak();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [deleteMembershipModal, setDeleteMembershipModal] = useState({
    isOpen: false,
    membershipId: '',
    userId: ''
  });
  const [addMembershipModalOpen, setAddMembershipModalOpen] = useState(false);
  const { workspaceId: fallbackWorkspaceId, isLoadingWorkspace } =
    useWorkspace();
  const workspaceId = providedWorkspaceId ?? fallbackWorkspaceId;
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [searchValue, setSearchValue] = useState('');
  const [selectedUser, setSelectedUser] = useState<IWorkspaceMembership | null>(
    null
  );

  const { watch, resetField, reset, setValue } = useForm<IMemberFilter>({
    defaultValues
  });

  const filters = watch();
  const offset = (page - 1) * pageSize;

  const { data, isLoading, isError, error } = useQuery({
    retry: 0,
    queryKey: [
      'workspace-memberships',
      { workspaceId, page, pageSize, filters }
    ],
    queryFn: () =>
      fetchWorkspaceMemberships({
        workspaceId,
        limit: pageSize,
        offset,
        filters: {
          searchParams: filters.searchParams
        }
      })
  });

  const removeWorkspaceMembershipMutation =
    removeUserFromWorkspace(workspaceId);
  const addWorkspaceMembershipMutation = addUsersToWorkspace(workspaceId);
  const grantOrgAdminMutation = grantOrgAdminToUser(workspaceId);
  const revertOrgAdminMutation = revertOrgAdminFromUser(workspaceId);
  const [adminActionModal, setAdminActionModal] = useState<{
    isOpen: boolean;
    email: string;
    userId: string;
    action: AdminAction;
  }>({
    isOpen: false,
    email: '',
    userId: '',
    action: 'grant'
  });

  const { isAllowed: isOrgAdminAllowed } = useAuthAccessQuery({
    relation: 'role_manager',
    entityType: 'workspace',
    entityId: workspaceId ?? ''
  });

  if (isLoadingWorkspace && !workspaceId) return <Spinner />;
  if (!workspaceId) return <div>Workspace not found</div>;
  if (isError) return isForbiddenError(error) ? <Forbidden /> : <NotFound />;

  const onCloseRemoveModal = () => {
    setDeleteMembershipModal({
      isOpen: false,
      membershipId: '',
      userId: ''
    });
  };

  const onCloseAdminActionModal = () => {
    setAdminActionModal({
      isOpen: false,
      email: '',
      userId: '',
      action: 'grant'
    });
  };

  const openAdminActionModal = (
    action: AdminAction,
    email: string | undefined,
    userId: string
  ) => {
    if (!email) {
      return toast({
        variant: 'error',
        title:
          action === 'grant' ? 'Cannot mark as admin' : 'Cannot revert admin',
        description: 'This member does not have an email address.',
        isClosable: true
      });
    }

    setAdminActionModal({
      isOpen: true,
      email,
      userId,
      action
    });
  };

  const onRemove = async () => {
    try {
      const hasOwnedSubmissions = await isSubmissionOwner(
        workspaceId,
        deleteMembershipModal.userId
      );
      if (hasOwnedSubmissions)
        return toast({
          variant: 'error',
          title: "Can't Remove Member",
          description:
            'This member is currently assigned to Submissions. To remove this user, first reassign their Submissions to another Editor.',
          isClosable: true
        });

      const res = await removeWorkspaceMembershipMutation.mutateAsync(
        deleteMembershipModal.membershipId
      );

      if (res.isSuccess) {
        await queryClient.invalidateQueries({
          queryKey: ['workspace-memberships']
        });

        toast({
          variant: 'success',
          title: 'Member Removed from Workspace',
          position: 'bottom-right',
          isClosable: true
        });

        onCloseRemoveModal();
        return;
      }
      return toast({
        variant: 'error',
        title: res?.message ?? 'Failed to remove member',
        isClosable: true
      });
    } catch (error) {
      return toast({
        variant: 'error',
        title: 'Failed to remove member',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to remove member. Please try again.',
        isClosable: true
      });
    }
  };

  const onConfirmAdminAction = async () => {
    const { email, action } = adminActionModal;
    if (!email) {
      return toast({
        variant: 'error',
        title:
          action === 'grant' ? 'Cannot mark as admin' : 'Cannot revert admin',
        description: 'This member does not have an email address.',
        isClosable: true
      });
    }

    const mutation =
      action === 'grant' ? grantOrgAdminMutation : revertOrgAdminMutation;
    const copy = adminActionCopy[action];

    try {
      const res = await mutation.mutateAsync(email);

      if (res.isSuccess) {
        toast({
          variant: 'success',
          title: copy.successTitle,
          position: 'bottom-right',
          isClosable: true
        });
        onCloseAdminActionModal();
        return;
      }

      return toast({
        variant: 'error',
        title:
          (typeof res?.message === 'object'
            ? res?.message?.message
            : res?.message) ??
          (action === 'grant'
            ? 'Failed to mark as admin'
            : 'Failed to revert admin'),
        isClosable: true
      });
    } catch (error) {
      return toast({
        variant: 'error',
        title:
          action === 'grant'
            ? 'Failed to mark as admin'
            : 'Failed to revert admin',
        description:
          error instanceof Error
            ? error.message
            : 'Something went wrong. Please try again.',
        isClosable: true
      });
    }
  };

  const onAdd = async (members: IWorkspaceMember[], emailNotify: boolean) => {
    try {
      const res = await addWorkspaceMembershipMutation.mutateAsync({
        members,
        emailNotify
      });

      if (res.isSuccess) {
        const { successCount, failureCount } = res;
        const emailDescription = emailNotify
          ? 'Invited Members will receive invitations to join this Workspace via email.'
          : undefined;
        if (failureCount === 0) {
          toast({
            variant: 'success',
            title: `${successCount} Member${
              successCount !== 1 ? 's' : ''
            } Added`,
            description: emailDescription,
            isClosable: true
          });
        } else {
          toast({
            variant: 'attention',
            title: `${successCount} Member${
              successCount !== 1 ? 's' : ''
            } Added, ${failureCount} Invitation Skipped`,
            description: emailNotify
              ? 'Invited Members will receive invitations to join this Workspace via email. Our system skipped any invites for existing members.'
              : 'Our system skipped any invites for existing members.',
            isClosable: true
          });
        }

        await queryClient.invalidateQueries({
          queryKey: ['workspace-memberships']
        });

        return setAddMembershipModalOpen(false);
      } else {
        return toast({
          variant: 'attention',
          title: 'Attention',
          description:
            (typeof res?.message === 'object'
              ? res?.message?.message
              : res?.message) ?? 'Failed to add members',
          isClosable: true
        });
      }
    } catch (error) {
      return toast({
        variant: 'attention',
        title: 'Attention',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to add members. Please try again.',
        isClosable: true
      });
    }
  };

  const addMemberButton = (
    <Button
      iconLeft={<FontAwesomeIcon icon={faUserPlus} />}
      onClick={() => setAddMembershipModalOpen(true)}
    >
      Add Members
    </Button>
  );

  const isEmpty = data?.total === 0;
  const totalMembers = data?.total ?? 0;
  const startIndex = offset + 1;
  const endIndex = Math.min(offset + pageSize, totalMembers);
  const hasFilters = !!filters.searchParams;

  const clearFilters = () => {
    reset(defaultValues);
    setSearchValue('');
  };

  const filtersKey = useMemo(
    () =>
      JSON.stringify({
        searchParams: (filters.searchParams ?? '').trim()
      }),
    [filters.searchParams]
  );

  useEffect(() => {
    setPage(1);
  }, [filtersKey]);

  return (
    <>
      {selectedUser?.id && selectedUser?.user?.status === 'active' ? (
        <SubmittedAuthorDetailsModal
          backButtonText="Back to Workspace Members"
          userId={selectedUser.userId}
          onBackClick={() => setSelectedUser(null)}
          roles={selectedUser.roles}
          isProxyUserNotRequired={true}
          user={selectedUser.user}
        />
      ) : null}
      <Flex flexDirection={'column'} gap={4} width="100%">
        <Flex flexDirection={'column'} gap={2} mb={2}>
          <Flex gap={4} flexDirection={{ base: 'column', md: 'row' }}>
            <Flex gap={2} alignItems="center" flex="1">
              <SearchInput
                placeholder="Search members by name and email"
                iconLeft={<FontAwesomeIcon icon={faSearch} />}
                width="100%"
                name="searchParams"
                value={searchValue}
                enterKeyHint="search"
                onChange={e => setSearchValue(e.currentTarget.value)}
                onInputClear={() => {
                  setSearchValue('');
                  resetField('searchParams');
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    setValue('searchParams', searchValue.trim());
                  }
                }}
                onKeyUp={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    setValue('searchParams', searchValue.trim());
                  }
                }}
              />
              <Box display={{ base: 'block', lg: 'none' }} flexShrink={0}>
                <Button
                  variant="primary"
                  aria-label="Search"
                  onClick={() => setValue('searchParams', searchValue.trim())}
                >
                  <FontAwesomeIcon icon={faSearch} />
                </Button>
              </Box>
            </Flex>
            {addMemberButton}
          </Flex>
        </Flex>
      </Flex>
      <Skeleton isLoaded={!isLoading}>
        <Flex alignItems={'center'} gap={2} height={5} marginTop={2}>
          <Text textStyle="textBody">
            {totalMembers > 0 ? (
              <>
                Showing <strong>{startIndex}</strong> -{' '}
                <strong>{endIndex}</strong> of <strong>{totalMembers}</strong>{' '}
                member
                {totalMembers === 1 ? '' : 's'}
              </>
            ) : (
              <>
                <strong>0</strong> members
              </>
            )}
          </Text>
          {hasFilters && (
            <ButtonText
              onClick={clearFilters}
              leftIcon={<FontAwesomeIcon icon={faDeleteRight} />}
              variant="secondary"
            >
              Clear filters
            </ButtonText>
          )}
        </Flex>
      </Skeleton>
      <Box width="100%" marginTop={2} overflowX="auto">
        {isEmpty ? (
          <Flex flexDirection={'column'} gap={2} alignItems="center">
            <Flex
              width={'100%'}
              padding={8}
              justifyContent="center"
              background="neutralSecondaryFill"
              borderRadius="lg"
              fontSize="48px"
            >
              <FontAwesomeIcon icon={faUserGroup} />
            </Flex>
            <Flex
              flexDirection={'column'}
              gap={2}
              alignItems="center"
              marginY={4}
            >
              <Text textStyle="headingGroup">No Members Added</Text>
              <Text textStyle="textBody">
                Build your Editorial Team by adding Workspace members.
              </Text>
            </Flex>
            {addMemberButton}
          </Flex>
        ) : (
          <Table size={'sm'}>
            <Thead>
              <Tr
                data-testid="member-table-header"
                gap={4}
                sx={{
                  '& th': {
                    textTransform: 'none',
                    color: 'black',
                    background: 'white'
                  }
                }}
              >
                <Th p={4}>
                  <Text textStyle="headingItem" whiteSpace="nowrap">
                    Member
                  </Text>
                </Th>
                <Th p={4}>
                  <Text textStyle="headingItem" whiteSpace="nowrap">
                    Workspace Role
                  </Text>
                </Th>
                <Th p={4}>
                  <Text textStyle="headingItem" whiteSpace="nowrap">
                    Status
                  </Text>
                </Th>
                <Th />
              </Tr>
            </Thead>
            <Tbody>
              {data?.collection.map(m => {
                const isActiveUser = m.user?.status === 'active';
                const isAdmin = !!m.roles?.includes('admin');
                const adminAction: AdminAction = isAdmin ? 'revert' : 'grant';
                const isAdminActionPending =
                  grantOrgAdminMutation.isPending ||
                  revertOrgAdminMutation.isPending;
                return (
                  <Tr
                    key={m.id}
                    alignItems="start"
                    _last={{ borderBottom: 'none' }}
                    data-testid="member-table-row"
                    gap={4}
                    background="white"
                  >
                    <Td
                      onClick={() => isActiveUser && setSelectedUser(m)}
                      cursor={isActiveUser ? 'pointer' : ''}
                      _hover={{ bg: isActiveUser ? 'gray.50' : '' }}
                    >
                      <Tooltip
                        hasArrow
                        borderRadius={4}
                        placement="top"
                        label={isActiveUser ? 'View User Profile' : ''}
                      >
                        <Flex flexDirection="column">
                          <Flex gap={2} alignItems="flex-end">
                            <Text
                              textStyle="headingLine"
                              textDecoration="none"
                              _hover={{
                                textDecoration: isActiveUser ? 'underline' : '',
                                textDecorationColor: 'actionPrimaryFill'
                              }}
                              color={isActiveUser ? 'actionPrimaryFill' : ''}
                            >
                              {m.user
                                ? `${m.user.firstName} ${m.user.lastName}`.trim()
                                : m.userId || 'Unknown user'}
                            </Text>

                            {currentUser.id === m.userId && (
                              <Badge size="context">You</Badge>
                            )}
                          </Flex>

                          <Text>{m.user?.email || 'No email'}</Text>
                        </Flex>
                      </Tooltip>
                    </Td>

                    <Td textStyle="headingLine">{m.displayRole ?? 'Editor'}</Td>

                    <Td textStyle="headingLine">
                      {userStatusBadgeMap[m.user?.status ?? 'invited']}
                    </Td>

                    <Td>
                      <Flex justifyContent="flex-end" width="100%">
                        <Menu aria-label="member actions menu">
                          <MenuButton>
                            <ButtonText>
                              <Flex gap={2} alignItems="center">
                                <FontAwesomeIcon size="lg" icon={faBars} />
                              </Flex>
                            </ButtonText>
                          </MenuButton>
                          <MenuList>
                            <MenuItem
                              ariaLabel={
                                isAdmin
                                  ? 'Remove Org Admin Access'
                                  : 'Mark As Org Admin'
                              }
                              disabled={
                                !isOrgAdminAllowed ||
                                isAdminActionPending ||
                                (isAdmin && currentUser.id === m.userId)
                              }
                              onClick={() =>
                                openAdminActionModal(
                                  adminAction,
                                  m.user?.email,
                                  m.userId
                                )
                              }
                            >
                              <Flex gap={2} alignItems="center">
                                <FontAwesomeIcon icon={faUserShield} />
                                <Text textStyle="context">
                                  {adminActionCopy[adminAction].buttonLabel}
                                </Text>
                              </Flex>
                            </MenuItem>

                            <MenuItem
                              ariaLabel="Remove Member"
                              disabled={currentUser.id === m.userId}
                              onClick={() =>
                                setDeleteMembershipModal({
                                  isOpen: true,
                                  membershipId: m.id,
                                  userId: m.userId
                                })
                              }
                            >
                              <Flex
                                gap={2}
                                color="actionSecondaryCautionContent"
                                alignItems="center"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                                <Text textStyle="context">Remove</Text>
                              </Flex>
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </Flex>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        )}
        <TablePagination
          ariaLabel="Members pagination"
          currentPage={page}
          totalItems={totalMembers}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </Box>

      <RemoveMember
        isOpen={deleteMembershipModal.isOpen}
        onClose={onCloseRemoveModal}
        onRemove={onRemove}
        isLoading={removeWorkspaceMembershipMutation.isPending}
      />
      <Modal
        isOpen={adminActionModal.isOpen}
        onClose={onCloseAdminActionModal}
        width={{ base: 'calc(100vw - 16px)', md: '450px' } as any}
        closeButtonProps={{
          isDisabled: true
        }}
      >
        <Flex flexDirection={'column'} gap={4} padding={{ base: 4, md: 8 }}>
          <Box fontSize="32px">
            <FontAwesomeIcon icon={faUserShield} />
          </Box>
          <Flex flexDirection={'column'} gap={2}>
            <Text textStyle="headingGroup">
              {adminActionCopy[adminActionModal.action].title}
            </Text>
            <Text textStyle="textBody">
              {adminActionCopy[adminActionModal.action].body}
            </Text>
          </Flex>
          <Flex gap={2} width="100%" justifyContent="flex-end">
            <Button
              variant="primary"
              onClick={onConfirmAdminAction}
              isDisabled={
                grantOrgAdminMutation.isPending ||
                revertOrgAdminMutation.isPending
              }
              isLoading={
                grantOrgAdminMutation.isPending ||
                revertOrgAdminMutation.isPending
              }
            >
              Yes
            </Button>
            <Button
              onClick={onCloseAdminActionModal}
              variant="secondary"
              isDisabled={
                grantOrgAdminMutation.isPending ||
                revertOrgAdminMutation.isPending
              }
            >
              No
            </Button>
          </Flex>
        </Flex>
      </Modal>
      <AddMembers
        onAdd={onAdd}
        isOpen={addMembershipModalOpen}
        onClose={() => setAddMembershipModalOpen(false)}
        isLoading={addWorkspaceMembershipMutation.isPending}
      />
    </>
  );
};

export default WorkspaceMembersPanel;
